// This file is responsible for drawing routes.
//  It does not draw the pins for the stops; only the lines between them.
//
// Ambassador: Yatharth.
//
import _ from 'lodash';
import { useEffect, useState } from 'react';

import { getGroupColor } from './mapColors';

// Erasing is just clearing the path. The polyline stays attached to the map,
//  ready to be reused with a new path later.
// The try/catch guards a race: if the polyline's map was torn down (see the
//  cleanup effect below), Google Maps' internals are gone and setPath() throws
//  "Cannot read properties of undefined (reading 'setAt')". Nothing left to erase.
const erasePolyline = (polyline: google.maps.Polyline) => {
    try {
        polyline.setPath([]);
    } catch {
        // Its map was torn down; nothing left to erase.
    }
};

// Detaching removes the polyline from the map entirely. Same teardown race as
//  above, so guard it the same way.
const detachPolyline = (polyline: google.maps.Polyline) => {
    try {
        polyline.setMap(null);
    } catch {
        // Already gone.
    }
};

// Use this function for each polyline you want to draw. When you call `.setPath()`
//  on a polyline for the second time, it will override the previously plotted path.
//  You can pass an empty path to erase the polyline entirely.
const makePolyline = ({
    map,
    maps,
    routeColor,
}: {
    map: google.maps.Map;
    maps: typeof google.maps;
    routeColor: string;
}): google.maps.Polyline =>
    new maps.Polyline({
        map,
        path: [],
        strokeColor: routeColor,
        strokeOpacity: 0.9,
        strokeWeight: 7,
    });

export const useDrawRoutes = ({
    map,
    maps,
    routeResults: results,
    drawRoutes,
}: {
    map: google.maps.Map | null;
    maps: typeof google.maps | null;
    routeResults: (google.maps.routes.Route | null)[];
    drawRoutes: boolean;
}) => {
    //

    // Polylines are the things that draw the lines on the map.
    //  We need to have at least N of them in stock to draw N lines.
    const [polylines, setPolylines] = useState<google.maps.Polyline[]>([]);

    // Polylines are bound to a specific google.maps.Map. When that map instance is
    //  swapped (@react-google-maps/api recreates it on re-mount) or torn down,
    //  leftover polylines point at destroyed internals and calling setPath() on
    //  them throws ("... reading 'setAt'"). Detach and drop them so the draw effect
    //  rebuilds fresh polylines against the current map. This also prevents a leak
    //  on unmount, matching the setMap(null) cleanup used elsewhere (useSpiderfy).
    useEffect(() => {
        return () => {
            // The updater callback still runs during unmount, so the detach side
            //  effect fires there too.
            setPolylines((prev) => {
                prev.forEach(detachPolyline);
                return [];
            });
        };
    }, [map, maps]);

    useEffect(() => {
        //

        // If `map` or `maps` is null, then Google Maps is not ready yet.
        //  No worries. This hook will get called again with the new `map`
        //  and `maps` parameters when they get set, and this effect will rerun.
        if (!map || !maps) {
            return;
        }

        // If we're not drawing routes, erase everything!
        if (!drawRoutes) {
            polylines.forEach(erasePolyline);
            return;
        }

        // If we don't have enough polylines, we need to make some more.
        //  then let's make some more.
        if (polylines.length < results.length) {
            // We make sure to iterate over [polylines.length, results.length),
            //  and not [0, results.length - polylines.length) -- even though
            //  those ranges have the same length.
            // This is because we base the color of the polyline based on the group
            //  index. We have to account for the fact that the first `polylines.length`
            //  colors have already been used up.
            const newPolylines = _.range(polylines.length, results.length).map((groupIndex) =>
                makePolyline({
                    map,
                    maps,
                    routeColor: getGroupColor(groupIndex),
                }),
            );

            setPolylines((prevPolylines) => [...prevPolylines, ...newPolylines]);

            // We do an early return. When `setPolylines()` finishes mutating the state,
            //  this effect will run again, because `polylines` will change, which this
            //  effect depends on.
            return;
        }

        // Now let's draw!
        results.forEach((result, groupIndex) => {
            const polyline = polylines[groupIndex];
            // A null result means we have no route for this group yet; erase
            //  the polyline instead of drawing a stale path.
            if (result?.path) {
                try {
                    polyline.setPath(result.path);
                } catch {
                    // Polyline's map was torn down between renders; skip it.
                }
            } else {
                erasePolyline(polyline);
            }
        });

        // Don't forget to erase any polylines not in use, or we'll see phantom routes on the map.
        polylines.slice(results.length).forEach(erasePolyline);

        //
    }, [map, maps, results, drawRoutes, polylines]);
};
